import React, { useState, useEffect, useRef } from "react";
import { base44 } from "@/api/base44Client";
import Seo from "@/components/Seo";
import ServiceHero from "@/components/marketing/service/ServiceHero";
import HowItWorksLedger from "@/components/marketing/service/HowItWorksLedger";
import WhatsIncluded from "@/components/marketing/service/WhatsIncluded";
import GuaranteeBand from "@/components/marketing/service/GuaranteeBand";
import OutletGrid from "@/components/marketing/service/OutletGrid";
import SectorFilterTabs from "@/components/marketing/service/SectorFilterTabs";
import SamplePlacements from "@/components/marketing/service/SamplePlacements";
import ServiceFAQ from "@/components/marketing/service/ServiceFAQ";
import CTASection from "@/components/marketing/CTASection";

export default function GuaranteedPlacements() {
  const [activeSector, setActiveSector] = useState("All");
  const [sectorCache, setSectorCache] = useState({});
  const [sectorErrors, setSectorErrors] = useState({});
  const fetchedRef = useRef(new Set());

  const fetchSector = (sector) => {
    if (fetchedRef.current.has(sector)) return;
    fetchedRef.current.add(sector);
    base44.functions
      .invoke("getSectorOutlets", { sector, count: 24 })
      .then((res) => {
        const data = res?.data || res || {};
        setSectorCache((prev) => ({
          ...prev,
          [sector]: { outlets: data.outlets || [], available: data.available ?? 0 },
        }));
      })
      .catch(() => {
        setSectorErrors((prev) => ({ ...prev, [sector]: true }));
      });
  };

  // Fetch "All" on mount
  useEffect(() => {
    fetchSector("All");
  }, []);

  // Fetch sector on-demand when tab changes
  useEffect(() => {
    fetchSector(activeSector);
  }, [activeSector]);

  const sectorData = sectorCache[activeSector];
  const hasError = sectorErrors[activeSector];
  const displayedOutlets = sectorData?.outlets || [];
  const isLoading = !sectorData && !hasError;
  const sparseNotice = sectorData && sectorData.available === 0
    ? `No ${activeSector} publications are currently available. See the full outlet list for all options.`
    : null;

  const handleRetry = () => {
    setSectorErrors((prev) => ({ ...prev, [activeSector]: false }));
    fetchedRef.current.delete(activeSector);
    fetchSector(activeSector);
  };

  return (
    <div className="bg-canvas">
      <Seo title="Global Press Release Distribution | Digital24" />
      <ServiceHero
        eyebrow="Guaranteed Placements"
        title="Guaranteed Coverage on Named Publications"
        subtitle="Guaranteed placements put your article on a specific, named publication you choose. You pick the outlet, we handle the placement. Published, indexed, and fully attributed."
        primaryCTA={{ label: "View Outlet Pricing", to: "/media-network" }}
        secondaryCTA={{ label: "Request Custom Package", to: "/contact" }}
      />
      <OutletGrid
        outlets={displayedOutlets}
        gridKey={activeSector}
        filter={<SectorFilterTabs active={activeSector} onChange={setActiveSector} />}
        loading={isLoading}
        error={hasError}
        onRetry={handleRetry}
        notice={sparseNotice}
        title="One of the strongest placement lists in the market"
        subtitle="A selection from our full list, spanning high-authority publications across finance, tech, crypto, business, lifestyle, and more, in every major region. See the full list for availability and pricing."
        formatsNote="Formats vary by publication: many accept press releases, contributor articles, or staff-written coverage. Availability is shown per outlet."
        cta={{ label: "See all outlets and pricing", to: "/outlets" }}
      />
      <WhatsIncluded />
      <GuaranteeBand />
      <SamplePlacements />
      <HowItWorksLedger />
      <ServiceFAQ />
      <CTASection />
    </div>
  );
}